/**
 * Alat porositas total — metodenya, dan angka yang dibaca metode itu saja.
 *
 * Sebangun dengan `VshTool`, dan dipakai oleh dua layar yang sama: rel
 * parameter di ruang kerja dan tahap kedua di rel alur deterministik.
 *
 * Yang membedakannya dari daftar metode biasa adalah bahwa dua pilihan di sini
 * bukan metode melainkan cara sebuah metode dihitung — lengkung sonik (Wyllie
 * atau Raymer-Hunt) dan rata-rata pasangan densitas-neutron (RMS yang menahan
 * silang gas, atau rata-rata biasa). Keduanya sudah ada di mesin sejak lama;
 * yang belum ada adalah cara memilihnya dari luar, dan sebuah kemampuan yang
 * tidak bisa dicapai sama saja dengan kemampuan yang tidak ada.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import {
  CurveSourceFields,
  channelsRead,
  sameParam,
  type AvailableCurve,
} from "./CurveSource";
import { Button, Dropdown, Modal, NumberField, ParamCaption, Toggle } from "./ui";

/**
 * Kurva yang dibaca sebagai porositas total.
 *
 * Urutannya urutan pilihan, bukan abjad: pasangan densitas-neutron lebih dulu
 * sebab ia yang dipakai kecuali ada alasan untuk tidak, dan alasan itu selalu
 * sebuah kurva yang diragukan.
 */
export const PHIT_METHODS = [
  { value: "density_neutron", label: "Density–neutron", hint: "RHOB + NPHI" },
  { value: "density", label: "Density", hint: "RHOB" },
  { value: "neutron", label: "Neutron", hint: "NPHI" },
  { value: "sonic", label: "Sonic", hint: "DTC" },
];

/** Dua lengkung porositas sonik, dan keduanya memang berpotongan. */
export const PHIT_SONIC_METHODS = [
  { value: "wyllie", label: "Wyllie", hint: "time average" },
  { value: "raymer_hunt", label: "Raymer–Hunt–Gardner", hint: "unconsolidated sand" },
];

/**
 * Rumus tiap pilihan, ditulis sebagaimana ia dihitung di mesin.
 *
 * Kuncinya metode, kecuali sonik yang bergantung pada lengkungnya dan
 * densitas-neutron yang bergantung pada rata-ratanya; lihat `phitFormula`.
 */
export const PHIT_FORMULAS: Record<string, string> = {
  density: "PHID = (RHOma − RHOB) / (RHOma − RHOfl)",
  neutron: "PHIT = NPHI",
  wyllie: "PHIS = (DT − DTma) / (DTfl − DTma) / Bcp",
  raymer_hunt: "PHIS = 0.625 (1 − DTma / DT)",
  rms: "PHIT = √((PHID² + PHIN²) / 2) across gas crossover",
  mean: "PHIT = (PHID + PHIN) / 2",
};

/** Rumus yang berlaku bagi sebuah setelan penuh, bukan bagi metodenya saja. */
export const phitFormula = (params: ParameterValues): string => {
  const method = String(params.phit_method ?? "density_neutron");
  if (method === "sonic") {
    return PHIT_FORMULAS[String(params.phit_sonic_method ?? "wyllie")] ?? "";
  }
  if (method === "density_neutron") {
    return PHIT_FORMULAS[params.phit_gas_corrected === false ? "mean" : "rms"];
  }
  return PHIT_FORMULAS[method] ?? "";
};

/**
 * Keterangan bagi pilihan yang membaca sesuatu selain kotak di bawahnya.
 *
 * Neutron tidak membaca satu pun ujung — ia dipakai apa adanya — dan sebuah
 * panel yang kosong ketika sebuah pilihan dipilih membaca seperti pilihan yang
 * tidak bekerja.
 */
export const PHIT_NOTES: Record<string, string> = {
  neutron:
    "NPHI is read as porosity as it stands, with no matrix and no fluid. It " +
    "therefore reads too high in shale and too low in gas: what the next stage " +
    "corrects with Vsh is only the first of the two.",
  raymer_hunt:
    "Raymer–Hunt–Gardner reads neither DTfl nor a compaction factor — the form " +
    "of the equation has neither. It curves where Wyllie is straight, and at " +
    "the high-porosity end it reads below it: that is where Wyllie overstates " +
    "on unconsolidated sand.",
};

/**
 * Metode beserta angka yang dibacanya, tanpa menyimpan apa pun sendiri.
 *
 * Kotak yang muncul mengikuti metode yang dipilih, dengan alasan yang sama
 * seperti pada Vsh: RHOma di sebelah porositas sonik adalah angka yang tidak
 * menentukan apa pun pada hasilnya.
 */
export function PhitFields({
  params,
  available,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  /** Mnemonik yang ada di set kurva aktif sumur ini. Tanpa itu tidak ada
   *  pemilih sumber sama sekali — rel ruang kerja memanggil komponen ini
   *  di tempat yang belum memuat daftar kurvanya. */
  available?: AvailableCurve[];
  onChange: (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) => void;
  disabled?: boolean;
}) {
  const method = String(params.phit_method ?? "density_neutron");
  const sonic = String(params.phit_sonic_method ?? "wyllie");
  const note = PHIT_NOTES[method === "sonic" ? sonic : method];
  return (
    <>
      <ParamCaption>Method</ParamCaption>
      <Dropdown
        value={method}
        options={PHIT_METHODS}
        onChange={(value) => onChange("phit_method", value)}
        width="100%"
        disabled={disabled}
      />
      {method === "sonic" && (
        <>
          <ParamCaption>Transform</ParamCaption>
          <Dropdown
            value={sonic}
            options={PHIT_SONIC_METHODS}
            onChange={(value) => onChange("phit_sonic_method", value)}
            width="100%"
            disabled={disabled}
          />
        </>
      )}
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-4)" }}>
        {phitFormula(params)}
      </span>

      {(method === "density" || method === "density_neutron") && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <NumberField
            label="RHOma"
            unit="g/cc"
            value={Number(params.rho_matrix)}
            step={0.01}
            onChange={(v) => onChange("rho_matrix", v)}
            disabled={disabled}
          />
          <NumberField
            label="RHOfl"
            unit="g/cc"
            value={Number(params.rho_fluid)}
            step={0.01}
            onChange={(v) => onChange("rho_fluid", v)}
            disabled={disabled}
          />
        </div>
      )}

      {method === "sonic" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <NumberField
            label="DTma"
            unit="us/ft"
            value={Number(params.dt_matrix)}
            step={0.5}
            onChange={(v) => onChange("dt_matrix", v)}
            disabled={disabled}
          />
          {/* Kedua kotak ini hanya dibaca Wyllie. Menampilkannya di sebelah
              Raymer-Hunt adalah dua angka yang tidak mengubah apa pun. */}
          {sonic === "wyllie" && (
            <NumberField
              label="DTfl"
              unit="us/ft"
              value={Number(params.dt_fluid)}
              step={1}
              onChange={(v) => onChange("dt_fluid", v)}
              disabled={disabled}
            />
          )}
        </div>
      )}
      {method === "sonic" && sonic === "wyllie" && (
        <NumberField
          label="Compaction factor Bcp"
          value={Number(params.phit_compaction ?? 1)}
          step={0.05}
          min={1}
          onChange={(v) => onChange("phit_compaction", v)}
          disabled={disabled}
        />
      )}

      {method === "density_neutron" && (
        <Toggle
          label="Gas crossover correction (RMS)"
          checked={params.phit_gas_corrected !== false}
          onChange={(checked) => onChange("phit_gas_corrected", checked)}
          disabled={disabled}
        />
      )}

      {note && (
        <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
          {note}
        </span>
      )}
      {available && (
        <CurveSourceFields
          params={params}
          available={available}
          channels={channelsRead(2, params)}
          onChange={(sources) => onChange("curve_sources", sources)}
          disabled={disabled}
        />
      )}

    </>
  );
}

/** Kunci yang boleh berangkat dari jendela ini; sisanya bukan urusan PHIT. */
const PHIT_KEYS: (keyof ParameterValues)[] = [
  "phit_method", "phit_sonic_method", "phit_compaction", "phit_gas_corrected",
  "rho_matrix", "rho_fluid", "dt_matrix", "dt_fluid",
  "curve_sources",
];

/**
 * Tahap porositas total sebagai jendelanya sendiri.
 *
 * Aturannya sama dengan jendela Vsh: yang dikirim pulang hanyalah yang
 * benar-benar diubah, sebab tiap run dengan override menerbitkan versi
 * parameter set baru dan riwayat yang menyebut angka yang tidak berubah adalah
 * riwayat yang lebih sulit dibaca daripada tidak ada riwayat.
 */
export function PhitDialog({
  params,
  available,
  busy = false,
  disabled = false,
  onClose,
  onRun,
}: {
  params: ParameterValues;
  available?: AvailableCurve[];
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onRun: (overrides: Partial<ParameterValues>) => void;
}) {
  const [draft, setDraft] = useState<Partial<ParameterValues>>({});
  const merged = { ...params, ...draft } as ParameterValues;
  const changed = Object.fromEntries(
    PHIT_KEYS.filter((key) => key in draft && !sameParam(draft[key], params[key])).map(
      (key) => [key, draft[key]],
    ),
  ) as Partial<ParameterValues>;

  return (
    <Modal
      title="2 · Total porosity"
      width={420}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled}
            onClick={() => onRun(changed)}
          >
            Compute porosity
          </Button>
        </>
      }
    >
      <PhitFields
        params={merged}
        available={available}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        disabled={disabled || busy}
      />
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {"The whole deterministic workflow is run again. Effective porosity is " +
          "PHIT less its shale part, saturation is read from that effective " +
          "porosity, and the cutoffs decide on both."}
      </span>
    </Modal>
  );
}
